import React, { useMemo, useState, useCallback } from "react";
import Table from "../../../../components/Table";
import TitleButtons from "../../../../components/TitleButtons";
import LeftTable from "../../../../components/LeftTable";
import { useStandbySuprimentos } from "./standbyData-servico";
import Modal from "../../../../components/Modal";
import { generatePdfReport } from "../../../../utils/pdf/Report-Standby-Technical";
import { generatePdfReportAllOS } from "../../../../utils/pdf/Report-Standby-All-OS";
import ModalItemsGrid from "../../../../components/ModalItemsGrid";

type Row = (string | number | React.ReactNode)[];

const IDX = {
  OS: 0,
  TIPO: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  TECNICO: 6,
  DATA: 7,
  HORA: 8,
  // 9 = "Dias de Atraso"
};

/* ================= Helpers ================= */
function parseBRL(s: any) {
  return typeof s === "string"
    ? Number(
        String(s)
          .replace(/[^\d,.-]/g, "")
          .replace(/\./g, "")
          .replace(",", ".")
      )
    : Number(s ?? 0);
}
function toStartOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function formatFirstTwoNames(nome: string | null | undefined): string {
  if (!nome) return "-";
  const parts = String(nome).trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return parts.join(" ");
  return `${parts[0]} ${parts[1]}`;
}
function parseDateSmart(s: string): Date | null {
  if (!s) return null;
  const t = s.trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(t); // dd/MM/yyyy
  if (m) {
    const dd = Number(m[1]);
    const MM = Number(m[2]) - 1;
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, MM, dd);
    return isNaN(d.getTime()) ? null : d;
  }
  const d2 = new Date(t);
  return isNaN(d2.getTime()) ? null : d2;
}
function diffDaysFromToday(dateStr: string): number | null {
  const d = parseDateSmart(dateStr);
  if (!d) return null;
  const today = toStartOfDay(new Date());
  const start = toStartOfDay(d);
  const ms = today.getTime() - start.getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

const API_BASE = "https://api.eva-googlemarine.com";
const URL_RETORNAR = `${API_BASE}/movimentar-produtos-retornar`;

type RawItem = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  moeda?: string | null;
  pendente_retorno?: boolean | null;
  preco_venda?: string | number | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao?: string | null;
  };
};

type RawMov = {
  id: number;
  responsavel_id: number | null;
  responsavel_nome: string | null;
  departamento_origem: string | null;
  tipo: string | null;
  os_numero?: string | null;
  coletor_nome?: string | null;
  criado_em: string;
  embarcacao?: string | null;
  comentario?: string | null;
  // alguns endpoints mandam isso:
  empresa?: { nome_fantasia?: string | null } | null;
  // e o seu JSON mandou isso:
  empresa_cliente?: string | null;
};

async function postJSON(url: string, payload: any) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} – ${t || "erro ao enviar"}`);
  }
  return res.json().catch(() => ({}));
}

async function askQuantidade(max: number): Promise<number | null> {
  const val = window.prompt(
    `Quantidade (máxima: ${max})`,
    String(Math.min(1, max))
  );
  if (val == null) return null;
  const n = Number(String(val).replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) {
    window.alert("Informe um número válido maior que zero.");
    return null;
  }
  if (!Number.isInteger(n)) {
    window.alert("A quantidade deve ser inteira.");
    return null;
  }
  if (n > max) {
    window.alert(`A quantidade não pode exceder ${max}.`);
    return null;
  }
  return n;
}

function mapUiToRawItem(items: RawItem[], uiRow: Row): RawItem | null {
  const codigo = String(uiRow[1] ?? "-");
  const nome = String(uiRow[2] ?? "-");
  const qtd = Number(uiRow[3] ?? 0);

  return (
    items.find(
      (it) =>
        it.produto?.codigo === codigo &&
        it.produto?.nome === nome &&
        Number(it.quantidade) === qtd
    ) ||
    items.find(
      (it) => it.produto?.codigo === codigo && Number(it.quantidade) === qtd
    ) ||
    items.find((it) => it.produto?.codigo === codigo) ||
    null
  );
}

const SuprimentosStandby: React.FC = () => {
  const headers = useMemo(
    () => [
      "OS",
      "C. Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Técnico",
      "Data",
      "Hora",
      "Dias de Atraso",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "60px",
      "80px",
      "70px",
      "300px",
      "10px",
      "100px",
      "100px",
      "80px",
      "60px",
      "70px",
    ],
    []
  );

  // ✅ IMPORTANTE: precisamos do rawByOS aqui
  const { rows, loading, refresh, rawByOS } = useStandbySuprimentos() as any;

  /* ===== LISTA: acrescenta “Dias de Atraso” e propaga pendência ===== */
  const tableDisplayRows = useMemo<Row[]>(() => {
    return (rows as any[]).map((r) => {
      const clone = (Array.isArray(r) ? r.slice() : []) as Row;

      const tecnicoRaw = String(clone[IDX.TECNICO] ?? "");
      clone[IDX.TECNICO] = formatFirstTwoNames(tecnicoRaw);

      const dataStr = String(r[IDX.DATA] ?? "");
      const days = diffDaysFromToday(dataStr);
      clone.push(days == null ? "-" : String(days)); // índice 9

      const pend =
        Boolean((r as any)?.__pendente_retorno) ||
        Boolean((r as any)?.pendente_retorno);
      (clone as any).__pendente_retorno = pend;

      return clone;
    });
  }, [rows]);

  /* ===== Modal open ===== */
  const [openOS, setOpenOS] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleDoubleClick = useCallback((_vi: number, rowData: Row) => {
    const os = String(rowData[IDX.OS] ?? "").trim();
    if (!os) return;
    setSelectedIndex(null);
    setOpenOS(os);
  }, []);

  // ✅ RAW do OS vindo do hook (mesma fonte da tabela)
  const rawOS = useMemo(() => {
    if (!openOS) return null;
    const key = String(openOS).trim();
    return rawByOS?.[key] ?? null;
  }, [openOS, rawByOS]);

  const mov: RawMov | null = useMemo(() => rawOS?.mov ?? null, [rawOS]);
  const rawItems: RawItem[] = useMemo(
    () => (Array.isArray(rawOS?.itens) ? rawOS.itens : []),
    [rawOS]
  );

  const responsavelId = useMemo(() => {
    return mov?.responsavel_id ?? null;
  }, [mov]);

  const embarcacaoNome = useMemo(() => {
    return String(mov?.embarcacao || "-").trim() || "-";
  }, [mov]);

  const empresaNome = useMemo(() => {
    // cobre os dois formatos (empresa.nome_fantasia ou empresa_cliente)
    const a = String(mov?.empresa?.nome_fantasia || "").trim();
    const b = String((mov as any)?.empresa_cliente || "").trim();
    return a || b || "-";
  }, [mov]);

  const osDetails = useMemo(() => {
    if (!openOS) return null;

    const itensUI = (rows as Row[]).filter(
      (r) => String(r[IDX.OS]).trim() === String(openOS).trim()
    );

    const base = {
      info: [] as { label: string; value: string }[],
      headers: [] as string[],
      tableRows: [] as Row[],
      colWidths: [] as string[],
      totalMateriais: 0,
      totalUnidades: 0,
      totalCusto: 0,
    };

    if (!itensUI.length) return base;

    const first = itensUI[0];
    const data = String(first[IDX.DATA] ?? "-");
    const hora = String(first[IDX.HORA] ?? "-");
    const tecnico = String(first[IDX.TECNICO] ?? "-");

    const totalUnidades = itensUI.reduce(
      (acc, r) => acc + Number(r[IDX.QTD] ?? 0),
      0
    );
    const totalMateriais = itensUI.length;
    const totalCusto = itensUI.reduce(
      (acc, r) => acc + parseBRL(r[IDX.CUSTO]) * Number(r[IDX.QTD] ?? 0),
      0
    );

    const info = [
      { label: "Número da O.S", value: openOS },
      { label: "Responsável/Técnico", value: tecnico || "-" },
      { label: "Empresa", value: empresaNome || "-" },
      { label: "Embarcação", value: embarcacaoNome || "-" },
      { label: "Data", value: data },
      { label: "Hora", value: hora },
      {
        label: "Total de Materiais Coletados",
        value: `${totalMateriais} itens, ${totalUnidades} unidades`,
      },
      {
        label: "Custo Total (aprox.)",
        value:
          isFinite(totalCusto) && totalCusto > 0
            ? totalCusto.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })
            : "-",
      },
      {
        label: "Origem",
        value: String(mov?.departamento_origem || "-"),
      },
    ];

    const detailsHeaders = [
      "C.Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Responsável",
      "Data",
      "Hora",
    ];
    const detailsColWidths = [
      "130px",
      "100px",
      "480px",
      "110px",
      "250px",
      "100px",
      "70px",
    ];

    const tableRows = itensUI.map((r) => [
      String(r[IDX.TIPO] ?? "-"),
      String(r[IDX.CODIGO] ?? "-"),
      String(r[IDX.NOME] ?? "-"),
      String(r[IDX.QTD] ?? "-"),
      String(r[IDX.TECNICO] ?? "-"),
      String(r[IDX.DATA] ?? "-"),
      String(r[IDX.HORA] ?? "-"),
    ]);

    return {
      info,
      headers: detailsHeaders,
      colWidths: detailsColWidths,
      tableRows,
      totalMateriais,
      totalUnidades,
      totalCusto,
    };
  }, [openOS, rows, empresaNome, embarcacaoNome, mov?.departamento_origem]);

  const handleGerarRelatorioDaOs = useCallback(async () => {
    if (!openOS || !osDetails)
      return window.alert("Abra uma O.S. para gerar o relatório completo.");
    if (!rawItems.length)
      return window.alert("Itens RAW da O.S. indisponíveis.");

    const tecnicos = (() => {
      const t = String(mov?.coletor_nome || "").trim();
      return t ? [t] : [];
    })();

    const materiais = rawItems.map((it) => ({
      codigo: it.produto?.codigo || "-",
      nome: it.produto?.nome || "-",
      quantidade: Number(it.quantidade ?? 0),
      custoUnitario: (() => {
        const rawStr = it.custo_unitario;
        if (rawStr == null) return null;
        const n = Number(String(rawStr).replace(",", "."));
        return Number.isFinite(n) ? n : null;
      })(),
      tecnico: mov?.coletor_nome || "-",
    }));

    try {
      await generatePdfReportAllOS({ osNumero: openOS, tecnicos, materiais });
    } catch (e: any) {
      window.alert(
        `Falha ao gerar o relatório completo da O.S.: ${e?.message || e}`
      );
    }
  }, [openOS, osDetails, rawItems, mov?.coletor_nome]);

  const handleRetornarOSInteira = useCallback(async () => {
    if (!openOS) return;

    // ✅ não desabilita: avisa exatamente o que falta
    if (!rawItems.length)
      return window.alert("Itens RAW não carregados para esta O.S.");
    if (responsavelId == null)
      return window.alert(
        "Sem responsável definido para esta O.S. (responsavel_id null)."
      );

    const itens = rawItems
      .filter((it) => Number(it.quantidade) > 0)
      .map((it) => ({
        movimentacao_id: it.movimentacao_id,
        produto_id: it.produto_id,
        quantidade: Number(it.quantidade),
      }));

    if (!itens.length)
      return window.alert("Não há itens com quantidade para retornar.");

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, { responsavel_id: responsavelId, itens });
      window.alert("Todos os itens foram retornados ao estoque.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar O.S.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openOS, rawItems, responsavelId, refresh]);

  const handleRetornarItem = useCallback(async () => {
    if (!openOS || !osDetails) return;
    if (selectedIndex == null)
      return window.alert("Selecione um material na lista.");
    if (!rawItems.length)
      return window.alert("Itens RAW não carregados para esta O.S.");
    if (responsavelId == null)
      return window.alert(
        "Sem responsável definido para esta O.S. (responsavel_id null)."
      );

    const ui = osDetails.tableRows[selectedIndex];
    if (!ui) return window.alert("Item selecionado não encontrado.");

    const rawItem = mapUiToRawItem(rawItems, ui);
    if (!rawItem)
      return window.alert(
        "Não foi possível localizar o item selecionado no RAW."
      );

    const max = Number(rawItem.quantidade ?? 0);
    if (!(max > 0))
      return window.alert("Item sem quantidade disponível para retorno.");

    const qtdSel = await askQuantidade(max);
    if (qtdSel == null) return;

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, {
        responsavel_id: responsavelId,
        itens: [
          {
            movimentacao_id: rawItem.movimentacao_id,
            produto_id: rawItem.produto_id,
            quantidade: qtdSel,
          },
        ],
      });
      window.alert("Retorno realizado com sucesso.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openOS, osDetails, selectedIndex, rawItems, responsavelId, refresh]);

  const handleModalClose = useCallback(() => {
    if (busy) return;
    setOpenOS(null);
  }, [busy]);

  return (
    <div>
      <TitleButtons title="Suprimentos - Standby Serviço" />

      <Table
        headers={headers}
        rows={tableDisplayRows}
        columnWidths={columnWidths}
        isTableStandby
        onDoubleClick={handleDoubleClick}
        enableCellColors={false}
        atrasoColumnIndex={9}
        delayScope="cell"
        enableZebra={false}
        isRowPending={(_row) => Boolean((_row as any)?.__pendente_retorno)}
        responsiveWidths={{
          1300: {
            1: "60px",
            2: "0px",
            3: "300px",
            4: "0px",
            5: "100px",
            6: "100px",
            7: "60px",
            8: "60px",
            9: "60px",
          },
        }}
      />

      <Modal open={!!openOS} onClose={handleModalClose} maxWidth={1280}>
        {openOS && osDetails && (
          <div
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 8,
              width: "100%",
            }}
          >
            <div style={{ width: "100%" }}>
              <LeftTable data={osDetails.info} />
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              {/* ✅ Só desabilita por busy. Se faltar algo, avisamos no clique */}
              <button
                disabled={busy}
                onClick={handleRetornarOSInteira}
                style={{
                  marginBottom: 8,
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #0a7",
                  background: "#f0fffa",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna todos os itens desta O.S. ao estoque"
              >
                Retornar O.S. ao estoque
              </button>

              <button
                disabled={busy}
                onClick={handleGerarRelatorioDaOs}
                style={{
                  marginBottom: 8,
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #C27B30FF",
                  background: "#FCF1E5FF",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Gerar relatório completo da O.S."
              >
                Gerar Relatório da O.S
              </button>
            </div>

            <ModalItemsGrid
              headers={osDetails.headers}
              colWidths={osDetails.colWidths}
              rows={osDetails.tableRows}
              selectedIndex={selectedIndex}
              onSelectIndex={setSelectedIndex}
              centerCols={[0, 1, 2, 3, 4, 5, 7, 8]}
              perColumnRender={{
                "Preço de Venda": (uiRow) => {
                  const found = mapUiToRawItem(rawItems, uiRow as Row);
                  const pv = found?.preco_venda;
                  const moeda = String(found?.moeda || "BRL").toUpperCase();

                  if (pv == null || pv === "") return "-";
                  if (String(pv).toLowerCase() === "pendente informar")
                    return "pendente informar";

                  const n = Number(String(pv).replace(",", "."));
                  if (!Number.isFinite(n)) return "-";

                  try {
                    return new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: moeda,
                    }).format(n);
                  } catch {
                    return `R$ ${n.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}`;
                  }
                },
                Moeda: (uiRow) => {
                  const found = mapUiToRawItem(rawItems, uiRow as Row);
                  return String(found?.moeda || "BRL").toUpperCase();
                },
              }}
            />

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 8,
              }}
            >
              {/* ✅ Só trava por busy / selectedIndex */}
              <button
                disabled={busy || selectedIndex == null}
                onClick={handleRetornarItem}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #08a",
                  background: "#f0f8ff",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna somente o item selecionado"
              >
                Retornar produto ao estoque
              </button>

              <button
                disabled={busy || selectedIndex == null}
                onClick={async () => {
                  try {
                    if (!openOS || !osDetails) return;
                    if (selectedIndex == null) return;

                    const ui = osDetails.tableRows[selectedIndex];
                    if (!ui) return;

                    await generatePdfReport({
                      osNumero: openOS,
                      tecnicoNome: String(ui[4] ?? "-"),
                      materiais: [
                        {
                          codigo: String(ui[1] ?? "-"),
                          nome: String(ui[2] ?? "-"),
                          quantidade: Number(ui[3] ?? 0),
                        },
                      ],
                    });
                  } catch (e: any) {
                    window.alert(
                      `Falha ao gerar o relatório: ${e?.message || e}`
                    );
                  }
                }}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #C27B30FF",
                  background: "#FCF1E5FF",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Gerar relatório da movimentação"
              >
                Gerar Relatório da Movimentação
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuprimentosStandby;
